import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import { LineChart, CombinedBarLineChart } from '../../../../../shared/components/charts';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
  ComparisonToggle,
  GranularityToggle,
  ComparisonColumns,
} from '../../../../../shared/components';
import { useSleepTrends, useSleepPatterns } from '../hooks';
import { SleepStatsColumn } from '../components/SleepStatsColumn';
import { buildFilterParams, transformTrendsData, useResolvedGranularity } from '../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonPatterns,
} from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';
import { buildComparisonTrendSeries, buildComparisonPatternSeries } from '../../../../../shared/utils';

const SLEEP_ANALYTICS_DETAILS = {
  sleepTrends: `Displays daily patterns of sleep stages over time, illustrating how time spent in light, deep, and REM sleep fluctuates across days or weeks. These trends provide insight into changes in sleep quality, restorative depth, and overall recovery capacity.

By tracking stage distribution longitudinally, this view helps identify consistent sleep behaviours, disruptions, or improvements linked to workload, stress, or lifestyle factors. Sustained reductions in deep or REM sleep may indicate compromised recovery, while stable or improving patterns suggest effective restorative processes and balanced physiological recovery.`,
  sleepStageBreakdown: `Shows the distribution of time spent in awake, light, REM, and deep sleep stages across the workforce. This breakdown provides insight into overall sleep architecture, highlighting the balance between restorative deep sleep, cognitive-supporting REM sleep, and lighter sleep phases. Monitoring this distribution helps assess sleep quality, recovery potential, and overall restorative adequacy across the organisation.`,
  sleepPatterns: `Provides a detailed weekly overview of sleep heart rate (displayed as columns) and sleep efficiency (displayed as a line), enabling comparison of nocturnal cardiovascular activity and sleep quality across each day of the week. Sleep heart rate reflects overnight autonomic load and recovery status, while sleep efficiency represents the proportion of time in bed spent asleep, indicating overall sleep consolidation.

Together, these metrics highlight patterns such as elevated overnight heart rate during periods of stress or strain, reductions in efficiency linked to disrupted sleep, and improvements associated with adequate recovery. Tracking these trends across the week helps identify behavioural influences, workload effects, and consistency of restorative sleep.`,
};

export const SleepAnalytics: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  const [sleepTrendView, setSleepTrendView] = useState('sleep-stages');

  // Map the Sleep Trends toggle to the metric whose tooltip should be shown.
  const sleepTrendMetricKey: Record<string, string> = {
    'sleep-stages': 'sleep_stage_deep',
    'total-sleep': 'hours_of_sleep',
    efficiency: 'sleep_efficiency',
    'sleep-debt': 'sleep_debt',
    'sleep-stress': 'sleep_stress',
  };

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId],
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  // Comparison mode: render one column/line per selected filter value.
  const comparison = useComparisonMode(filterState);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filterState,
    dateRange,
    effectiveClientId,
    comparison,
  );

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Sleep Analytics',
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: 'Sleep performance', keys: ['sleep_performance'] },
      { label: 'Sleep consistency', keys: ['sleep_consistency'] },
      { label: 'Sleep efficiency', keys: ['sleep_efficiency'] },
      { label: 'High sleep stress', keys: ['high_sleep_stress'] },
    ],
  });

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const trendsQuery = useSleepTrends({
    ...filterParams,
    metrics: [
      'sleep_efficiency',
      'sleep_debt',
      'sleep_stress',
      'hours_of_sleep',
      'sleep_stage_awake',
      'sleep_stage_light',
      'sleep_stage_rem',
      'sleep_stage_deep',
    ],
    granularity,
  });
  const hrPatternsQuery = useSleepPatterns({
    ...filterParams,
    metric: 'sleep_hr_patterns',
    granularity: 'weekly',
  });
  const efficiencyPatternsQuery = useSleepPatterns({
    ...filterParams,
    metric: 'sleep_efficiency_patterns',
    granularity: 'weekly',
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, [
      'sleep_efficiency',
      'sleep_debt',
      'sleep_stress',
      'hours_of_sleep',
      'sleep_stage_awake',
      'sleep_stage_light',
      'sleep_stage_rem',
      'sleep_stage_deep',
    ]);
  }, [trendsQuery.data]);

  // The metric the Sleep Trends chart focuses on for the current toggle. In
  // comparison mode we overlay this metric, one line per group. (The default
  // "Sleep Stages" stacked view focuses on deep sleep when comparing.)
  const trendFocusMetric = sleepTrendMetricKey[sleepTrendView] ?? 'sleep_stage_deep';

  // ── Comparison overlays ──────────────────────────────────────────────────
  const comparisonSleepTrends = useComparisonTrends(
    groups,
    [
      'sleep_efficiency',
      'sleep_debt',
      'sleep_stress',
      'hours_of_sleep',
      'sleep_stage_awake',
      'sleep_stage_light',
      'sleep_stage_rem',
      'sleep_stage_deep',
    ],
    granularity,
  );
  const comparisonEfficiencyPatterns = useComparisonPatterns(groups, 'sleep_efficiency_patterns', 'weekly');

  const sleepTrendCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonSleepTrends.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, [trendFocusMetric]),
        })),
        trendFocusMetric,
        'label',
      ),
    [comparisonSleepTrends, trendFocusMetric],
  );
  const sleepTrendCompareLoading = comparisonSleepTrends.some((e) => e.isLoading);

  // The "Sleep Stages" view is a stacked 4-series chart, which can't collapse to
  // a single overlaid line. In comparison mode we instead keep each group's full
  // stacked stages chart, side by side. Map group → its own stacked-stage rows.
  const STAGE_KEYS = ['sleep_stage_awake', 'sleep_stage_light', 'sleep_stage_rem', 'sleep_stage_deep'];
  const sleepStagesByGroup = useMemo(
    () =>
      comparisonSleepTrends.map((e) => ({
        group: e.group,
        data: transformTrendsData(e.data, STAGE_KEYS)
          .map((item) => ({
            label: item.label,
            sleep_stage_awake: item.sleep_stage_awake ?? null,
            sleep_stage_light: item.sleep_stage_light ?? null,
            sleep_stage_rem: item.sleep_stage_rem ?? null,
            sleep_stage_deep: item.sleep_stage_deep ?? null,
          }))
          .filter(
            (item) =>
              item.sleep_stage_awake !== null ||
              item.sleep_stage_light !== null ||
              item.sleep_stage_rem !== null ||
              item.sleep_stage_deep !== null,
          ),
      })),
    [comparisonSleepTrends],
  );

  const efficiencyPatternCompareChart = useMemo(
    () =>
      buildComparisonPatternSeries(
        comparisonEfficiencyPatterns.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: e.data?.data?.patterns ?? [],
        })),
        'day',
      ),
    [comparisonEfficiencyPatterns],
  );
  const efficiencyPatternCompareLoading = comparisonEfficiencyPatterns.some((e) => e.isLoading);

  const sleepStagesData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_stage_awake: item.sleep_stage_awake ?? null,
        sleep_stage_light: item.sleep_stage_light ?? null,
        sleep_stage_rem: item.sleep_stage_rem ?? null,
        sleep_stage_deep: item.sleep_stage_deep ?? null,
      }))
      .filter(
        (item) =>
          item.sleep_stage_awake !== null ||
          item.sleep_stage_light !== null ||
          item.sleep_stage_rem !== null ||
          item.sleep_stage_deep !== null,
      );
  }, [trendsData]);

  const efficiencyData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_efficiency: item.sleep_efficiency ?? null,
      }))
      .filter((item) => item.sleep_efficiency !== null);
  }, [trendsData]);

  const totalSleepData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        hours_of_sleep: item.hours_of_sleep ?? null,
      }))
      .filter((item) => item.hours_of_sleep !== null);
  }, [trendsData]);

  const sleepDebtData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_debt: item.sleep_debt ?? null,
      }))
      .filter((item) => item.sleep_debt !== null);
  }, [trendsData]);

  const sleepStressData = useMemo(() => {
    return trendsData
      .map((item) => ({
        label: item.label,
        sleep_stress: item.sleep_stress ?? null,
      }))
      .filter((item) => item.sleep_stress !== null);
  }, [trendsData]);

  // Extract absolute all-time min/max from the overall-range endpoint.
  // These values are scoped to the client and ignore any active date/filter selections.
  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      efficiencyMin: typeof metric_min.sleep_efficiency === 'number' ? metric_min.sleep_efficiency : undefined,
      efficiencyMax: typeof metric_max.sleep_efficiency === 'number' ? metric_max.sleep_efficiency : undefined,
      debtMin: typeof metric_min.sleep_debt === 'number' ? metric_min.sleep_debt : undefined,
      debtMax: typeof metric_max.sleep_debt === 'number' ? metric_max.sleep_debt : undefined,
      stressMin: typeof metric_min.sleep_stress === 'number' ? metric_min.sleep_stress : undefined,
      stressMax: typeof metric_max.sleep_stress === 'number' ? metric_max.sleep_stress : undefined,
      hrMin: typeof metric_min.resting_heart_rate === 'number' ? metric_min.resting_heart_rate : undefined,
      hrMax: typeof metric_max.resting_heart_rate === 'number' ? metric_max.resting_heart_rate : undefined,
      totalSleepMin: typeof metric_min.hours_of_sleep === 'number' ? metric_min.hours_of_sleep : undefined,
      totalSleepMax: typeof metric_max.hours_of_sleep === 'number' ? metric_max.hours_of_sleep : undefined,
    };
  }, [overallRangeQuery.data]);

  const combinedPatternsData = useMemo(() => {
    const hrPatterns = hrPatternsQuery.data?.data?.patterns || [];
    const efficiencyPatterns = efficiencyPatternsQuery.data?.data?.patterns || [];

    const hrMap = new Map<string, number | null>();
    hrPatterns.forEach((p) => {
      if (p.day) hrMap.set(p.day, p.value);
    });

    const efficiencyMap = new Map<string, number | null>();
    efficiencyPatterns.forEach((p) => {
      if (p.day) efficiencyMap.set(p.day, p.value);
    });

    const allDays = new Set<string>();
    hrPatterns.forEach((p) => {
      if (p.day) allDays.add(p.day);
    });
    efficiencyPatterns.forEach((p) => {
      if (p.day) allDays.add(p.day);
    });

    const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return Array.from(allDays)
      .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
      .map((day) => ({
        day,
        sleep_hr_patterns: hrMap.get(day) ?? null,
        sleep_efficiency_patterns: efficiencyMap.get(day) ?? null,
      }));
  }, [hrPatternsQuery.data, efficiencyPatternsQuery.data]);

  const isLoading = trendsQuery.isLoading || hrPatternsQuery.isLoading || efficiencyPatternsQuery.isLoading;

  const hasError = trendsQuery.isError || hrPatternsQuery.isError || efficiencyPatternsQuery.isError;

  // Stacked 4-series sleep-stages chart for a given dataset. Reused by both the
  // single (average) view and each per-group column in comparison mode.
  const renderStagesChart = (data: Array<Record<string, string | number | null>>, height = 300) =>
    data.length > 0 ? (
      <LineChart
        data={data}
        xAxisKey='label'
        series={[
          {
            dataKey: 'sleep_stage_deep',
            label: 'Slow Wave (Deep)',
            color: theme.palette.success.main,
            area: true,
            stack: 'sleep',
          },
          { dataKey: 'sleep_stage_rem', label: 'REM', color: theme.palette.warning.main, area: true, stack: 'sleep' },
          {
            dataKey: 'sleep_stage_light',
            label: 'Light Sleep',
            color: theme.palette.info.main,
            area: true,
            stack: 'sleep',
          },
          { dataKey: 'sleep_stage_awake', label: 'Awake', color: theme.palette.error.main, area: true, stack: 'sleep' },
        ]}
        height={height}
        yAxisMin={0}
        yAxisMax={10}
      />
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <Alert severity='info'>No sleep stages data available</Alert>
      </Box>
    );

  const renderSleepTrendChart = () => {
    if (sleepTrendView === 'sleep-stages') {
      return sleepStagesData.length > 0 ? (
        <LineChart
          data={sleepStagesData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'sleep_stage_deep',
              label: 'Slow Wave (Deep)',
              color: theme.palette.success.main,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_rem',
              label: 'REM',
              color: theme.palette.warning.main,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_light',
              label: 'Light Sleep',
              color: theme.palette.info.main,
              area: true,
              stack: 'sleep',
            },
            {
              dataKey: 'sleep_stage_awake',
              label: 'Awake',
              color: theme.palette.error.main,
              area: true,
              stack: 'sleep',
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={10}
          showMinMaxLines
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <Alert severity='info'>No sleep stages data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === 'total-sleep') {
      return totalSleepData.length > 0 ? (
        <LineChart
          data={totalSleepData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'hours_of_sleep',
              label: 'Total Sleep (hrs)',
              color: theme.palette.primary.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          yAxisMax={12}
          showMinMaxLines
          referenceMin={overallMinMax.totalSleepMin}
          referenceMax={overallMinMax.totalSleepMax}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <Alert severity='info'>No total sleep data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === 'efficiency') {
      return efficiencyData.length > 0 ? (
        <LineChart
          data={efficiencyData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'sleep_efficiency',
              label: 'Sleep Efficiency',
              color: theme.palette.success.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.efficiencyMin}
          referenceMax={overallMinMax.efficiencyMax}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <Alert severity='info'>No efficiency data available</Alert>
        </Box>
      );
    }

    if (sleepTrendView === 'sleep-debt') {
      return sleepDebtData.length > 0 ? (
        <LineChart
          data={sleepDebtData}
          xAxisKey='label'
          series={[
            {
              dataKey: 'sleep_debt',
              label: 'Sleep Debt',
              color: theme.palette.warning.main,
              area: true,
            },
          ]}
          height={300}
          yAxisMin={0}
          showMinMaxLines
          referenceMin={overallMinMax.debtMin}
          referenceMax={overallMinMax.debtMax}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <Alert severity='info'>No sleep debt data available</Alert>
        </Box>
      );
    }

    return sleepStressData.length > 0 ? (
      <LineChart
        data={sleepStressData}
        xAxisKey='label'
        series={[
          {
            dataKey: 'sleep_stress',
            label: 'Sleep Stress',
            color: theme.palette.error.main,
            area: true,
          },
        ]}
        height={300}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={overallMinMax.stressMin}
        referenceMax={overallMinMax.stressMax}
      />
    ) : (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Alert severity='info'>No sleep stress data available</Alert>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ComparisonToggle comparison={comparison} />
            <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && <Alert severity='error'>Failed to load sleep analytics data. Please try again later.</Alert>}

        {/* Stat cards + sleep-stage breakdown, one column per comparison group. */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => (
            <SleepStatsColumn
              filterParams={group.filterParams}
              isCentralAdmin={isCentralAdmin}
              isAnonymised={isAnonymised}
              stacked={isComparing}
            />
          )}
        </ComparisonColumns>

        <DashboardCard title='AI Summary' tooltip='AI narrative of the metrics shown.' expandable={false}>
          <AiSummaryPanel
            title={isComparing ? 'Compare these groups with AI' : 'Summarise this view with AI'}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Sleep Trends'
              subtitle='Daily patterns of sleep stages showing fluctuations in recovery and rest quality.'
              metricKey={sleepTrendMetricKey[sleepTrendView]}
              details={SLEEP_ANALYTICS_DETAILS.sleepTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={sleepTrendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setSleepTrendView(newValue);
                  }}
                  size='small'
                >
                  <ToggleButton value='sleep-stages'>Sleep Stages</ToggleButton>
                  <ToggleButton value='total-sleep'>Total Sleep</ToggleButton>
                  <ToggleButton value='efficiency'>Efficiency</ToggleButton>
                  <ToggleButton value='sleep-debt'>Sleep Debt</ToggleButton>
                  <ToggleButton value='sleep-stress'>Sleep Stress</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? sleepTrendCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  sleepTrendView === 'sleep-stages' ? (
                    // Stacked stages can't collapse to one line — show each
                    // group's full stages chart side by side instead.
                    <ComparisonColumns groups={groups}>
                      {(group, index) => {
                        const entry = sleepStagesByGroup[index];
                        return renderStagesChart(entry?.data ?? [], 260);
                      }}
                    </ComparisonColumns>
                  ) : sleepTrendCompareChart.data.length > 0 ? (
                    <LineChart
                      data={sleepTrendCompareChart.data}
                      xAxisKey='label'
                      series={sleepTrendCompareChart.series}
                      height={300}
                      yAxisMin={0}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No trend data available</Alert>
                    </Box>
                  )
                ) : (
                  renderSleepTrendChart()
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Sleep Stage Breakdown now renders inside each comparison column
            (SleepStatsColumn). Sleep Patterns overlays one efficiency line per
            group across the week in comparison mode. */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Sleep Patterns'
              subtitle='Overview of sleep heart rate (columns) and efficiency (line) trends segmented across the week.'
              metricKey='sleep_efficiency'
              details={SLEEP_ANALYTICS_DETAILS.sleepPatterns}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {(isComparing ? efficiencyPatternCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  efficiencyPatternCompareChart.data.length > 0 ? (
                    <LineChart
                      data={efficiencyPatternCompareChart.data}
                      xAxisKey='day'
                      series={efficiencyPatternCompareChart.series}
                      height={350}
                      yAxisMin={0}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No pattern data available</Alert>
                    </Box>
                  )
                ) : combinedPatternsData.length > 0 ? (
                  <CombinedBarLineChart
                    data={combinedPatternsData}
                    xAxisKey='day'
                    barSeries={[
                      {
                        dataKey: 'sleep_hr_patterns',
                        label: 'Heart Rate',
                        color: theme.palette.info.main,
                      },
                    ]}
                    lineSeries={[
                      {
                        dataKey: 'sleep_efficiency_patterns',
                        label: 'Efficiency',
                        color: theme.palette.success.main,
                      },
                    ]}
                    height={350}
                    barYAxisMin={0}
                    lineYAxisMin={0}
                    showMinMaxLines
                    referenceBarMin={overallMinMax.hrMin}
                    referenceBarMax={overallMinMax.hrMax}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
